import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchSubscriptionBannerImages } from 'src/services/apiRequests/SubscriptionBanner/FetchSubscriptionBannerImages';
import {
  getDisplayPlansData,
  getDisplayPlansMetaData
} from 'src/services/apiRequests/SubscriptionService';
import fetchGuestUserRegistrationToken from 'src/services/consumption/revised-subscription-journey/guest-user-registration-service';
import { PromoResponseItem } from 'src/services/responseTypes/applyPromoResponse';
import { BannerAPIContentType } from 'src/services/responseTypes/BannerApiResponse';
import { Data, Plan } from 'src/services/responseTypes/displayPlansResponse';
import { PlansMetaData } from 'src/services/responseTypes/plansMetaDataResponse';
import { colors } from 'src/styles/colors';
import {
  getLocalStorage,
  setLocalStorage
} from 'src/utils/browserStorageHandler';
import {
  disableBackgroundScrolling,
  enableBackgroundScrolling,
  resumePlayerPopup
} from 'src/utils/common';
import { ActivePromotions, DynamicObject } from 'src/utils/commonTypes';
import {
  CONSUMPTION_PAYMENT_JOURNEY_PATH,
  IS_CONSUMPTION_PAYMENT_JOURNEY,
  USER_TOKEN,
  USER_TYPE
} from 'src/utils/constants/localStorageConstants';
import {
  buyPlan,
  calculateDisplayPrice,
  calculateSellPrice,
  getCurrentPlan,
  getDaysInYearOrMonth,
  getPromoDiscount
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';
import { getBannerImageURL } from 'src/utils/SubscriptionHelper/SubscriptionImageBannerHelper';

import { useAppStore } from 'src/hooks/useAppStore';
import { MIXPANEL_BUY_PREMIUM } from '../../utils/analytics/mixpanel/mixpanelConstants';
import { HTTP_HEADER_KEYS } from '../../utils/constants/HttpMethodsConstants';
import { HeaderLinks } from '../../utils/constants/RouterConstants';
import {
  SINGLE_SPACE,
  SubscriptionComponentImageAlt,
  SubscriptionComponentImagePaths,
  UserTypes
} from '../../utils/constants/stringConstants';
import ApplyCodePopup from '../applyCodePopup/ApplyCodePopup';
import Button from '../buttons/Button';
import Modal from '../modal/Modal';
import HighestPlanPage from '../Premium-User/HighestPlanPage';
import {
  fireSubsPageViewedEvent,
  mixpanelPopupCTAEvent,
  subscriptionSelectedMixPanel
} from './MixpanelHelper';
import * as style from './style';

type componentProps = {
  showBuyPlanFlag: boolean;
  closeHandler: () => void;
};

const signInClickHandler = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  window.location.href = HeaderLinks.SIGN_IN;
};

const ConsumptionBuyPlanPopup = ({
  showBuyPlanFlag = false,
  closeHandler
}: componentProps) => {
  const {
    initialLaunchData,
    appConfig,
    usersLocationData,
    translations,
    usersData
  } = useAppStore();

  const { CODE_APPLIED_ICON, APPLY_CODE_ICON } = SubscriptionComponentImageAlt;
  const { CODE_APPLIED_ICON_IMAGE, APPLY_CODE_ICON_IMAGE } =
    SubscriptionComponentImagePaths;

  // local vars
  const xAccessToken = initialLaunchData?.platform_token?.token || '';
  const countryCode = usersLocationData?.country_code || '';
  const stateCode = usersLocationData?.state_code || '';
  const { subscription } = translations;

  // states
  const [bannerImages, setBannerImages] = useState<
    BannerAPIContentType[] | null
  >(null);
  const [plansData, setPlansData] = useState<Data | null>(null);
  const [plansMetaData, setPlansMetaData] = useState<PlansMetaData[]>([]);
  const [defaultPlan, setDefaultPlan] = useState<Data | null>(null);
  const [applyCodeFlag, setApplyCodeFlag] = useState(false);
  const [activePromos, setActivePromos] = useState<ActivePromotions>({});
  const [promoCode, setPromoCode] = useState('');
  const [prepaidCoupon, setPrepaidCoupon] = useState('');

  // refs
  const isPageViewedEventFired = useRef(false);

  useEffect(() => {
    //call banners api
    getBannerImages();

    // call display plans
    getDisplayPlans();
  }, []);

  useEffect(() => {
    disableBackgroundScrolling();
    return () => enableBackgroundScrolling();
  }, []);

  useEffect(() => {
    if (plansData) {
      const { default_plan, plans } = plansData;
      const getDefaultPlan =
        plans.filter((plan) => {
          if (plan?.id === default_plan) {
            return {
              ...plan,
              display_price: calculateDisplayPrice(plan, activePromos, true),
              actual_price: calculateSellPrice(plan, activePromos, true)
            };
          }
        }) || null;
      setDefaultPlan({ ...plansData, plans: getDefaultPlan });
      // place plans meta call once plans data is available
      (async () => {
        setPlansMetaData(await getDisplayPlansMetaData());
      })();

      if (!isPageViewedEventFired.current) {
        // fireSubsPageViewedEvent
        fireSubsPageViewedEvent();
        isPageViewedEventFired.current = true; // set ref to avoid event fire again when "plans" changes
      }
    }
  }, [plansData, activePromos]);

  /**
   * This component is Revised Subscription Journey pop-up thats visible on consumption page after triggering from BuyNow CTA below player banner.
   * One of the condition to show this component (hence the pop-up) is that User should not be premium. It is shown from register user.
   *
   * We trigger guest user registration flow only for guest user and not even for registered users. Later, if condition changes or this pop-up component is re-used
   * functionality should be re-visited to add proper checks.
   *
   * Not awaiting to keep it parallel call.
   */
  useEffect(() => {
    const userType = getLocalStorage(USER_TYPE);
    if (userType === UserTypes.GUEST) {
      fetchGuestUserRegistrationToken();
    }
  }, []);

  // handlers
  const applyCodeHandler = () => {
    setApplyCodeFlag(!applyCodeFlag);
  };

  const checkLoggedIn = (couponVal: string) => {
    setPrepaidCoupon(couponVal);
  };

  const playPauseClickHandler = () => {
    closeHandler();
    resumePlayerPopup();
  };

  const updateDiscount = (
    response: PromoResponseItem[],
    promoCodeValue: string
  ) => {
    const activePromotions: ActivePromotions = {};
    const currentTime = new Date();
    response.forEach((result) => {
      const selectedPromoCode = result.promotions.filter(
        (promoPlan) =>
          promoPlan.code.toLocaleLowerCase() ===
          promoCodeValue.toLocaleLowerCase()
      );
      result.promotions = selectedPromoCode;
      if (
        selectedPromoCode &&
        currentTime > new Date(selectedPromoCode[0].start_date) &&
        currentTime < new Date(selectedPromoCode[0].end_date)
      ) {
        activePromotions[result.id] = result;
      }
    });
    setActivePromos(activePromotions);
    setPromoCode(promoCodeValue);
  };

  // redirect to payments page with selected plan ion place
  const handlePaymentRedirection = () => {
    if (plansData) {
      const { default_plan } = plansData;
      const currentPlan = getCurrentPlan(plansData, default_plan);
      buyPlan(
        plansData,
        plansMetaData,
        currentPlan,
        default_plan,
        activePromos,
        promoCode,
        countryCode,
        null,
        true
      );
      // set indicator in localstorage for consumption buy plan journey
      setLocalStorage(IS_CONSUMPTION_PAYMENT_JOURNEY, true);
      setLocalStorage(
        CONSUMPTION_PAYMENT_JOURNEY_PATH,
        window.location.pathname
      );

      // analytics
      mixpanelPopupCTAEvent(MIXPANEL_BUY_PREMIUM);
      subscriptionSelectedMixPanel(currentPlan, MIXPANEL_BUY_PREMIUM);

      // redirect to payments page
      window.location.href = '/myaccount/payment';
    }
  };

  // display plans call
  const getDisplayPlans = async () => {
    const displayPlans = await getDisplayPlansData();
    if (displayPlans) {
      setPlansData(displayPlans);
    }
  };

  const renderCouponProperties = () => {
    const {
      CMS_CODEAPPLIED_SUBTEXT,
      INVALID_CODE_TEXT,
      CMS_BOTTOMSHEET_APPLYCODE_TEXT,
      PlanSelection_Cohort_CodeApplied_Header_Text,
      PlanSelection_Cohort_Header_Text
    } = subscription;

    const { default_plan = '' } = (plansData as Data) || {};

    let cohertDiscountedText = '';

    const currentPlan = getCurrentPlan(plansData, default_plan) as Plan;
    const currentPlanMeta: DynamicObject =
      (plansMetaData &&
        plansMetaData.find((plan) => plan.id === default_plan)) ||
      {};

    const {
      discount: { code = '', discountPercentage = '' } = {},
      title = ''
    } = currentPlan || {};

    const activePromoEnabled = Object.keys(activePromos).length;

    const renderIcon = !(activePromoEnabled || code);

    if (discountPercentage) {
      const duration = getDaysInYearOrMonth(
        currentPlanMeta?.billingFrequency,
        subscription
      );
      cohertDiscountedText = PlanSelection_Cohort_Header_Text.replace(
        '{{discount_value}}',
        discountPercentage
      )
        .replace('{{plan_title}}', title)
        .replace('{{plan_duration}}', duration);
    }

    let couponText = code
      ? PlanSelection_Cohort_CodeApplied_Header_Text.replace(
          '{{promo_code}}',
          code
        )
      : CMS_BOTTOMSHEET_APPLYCODE_TEXT;

    const imageproperties = {
      imageSrc:
        activePromoEnabled || code
          ? CODE_APPLIED_ICON_IMAGE
          : APPLY_CODE_ICON_IMAGE,
      imageAlt: activePromoEnabled || code ? CODE_APPLIED_ICON : APPLY_CODE_ICON
    };
    if (activePromoEnabled) {
      if (activePromos[currentPlan?.id]) {
        couponText = CMS_CODEAPPLIED_SUBTEXT.replace(
          '{{coupon_code_name}}',
          promoCode
        ).replace(
          '{{discounted_percentage}}',
          getPromoDiscount(activePromos[currentPlan?.id])
        );
      } else {
        couponText = promoCode + SINGLE_SPACE + INVALID_CODE_TEXT;
      }
    }

    return {
      renderIcon,
      imageproperties,
      couponText,
      disableClick: code || activePromoEnabled,
      isCohortDiscount: code?.length > 0,
      cohertDiscountedText
    };
  };

  // banner Images call
  const getBannerImages = async () => {
    const response = (await fetchSubscriptionBannerImages([
      HTTP_HEADER_KEYS.X_ACCESS_TOKEN
    ])) as BannerAPIContentType[];
    setBannerImages(response);
  };

  // render banner images, usecallBack - stops re-evaluation
  const renderBannerImages = useCallback(() => {
    return (
      <>
        {bannerImages && bannerImages.length > 0 ? (
          <div css={style.backgroundWrapper}>
            {bannerImages
              .slice(0, 4)
              .map((bannerImage: BannerAPIContentType) => {
                return (
                  <div css={style.backgroundGrid} key={bannerImage.id}>
                    <img
                      css={style.backgroundGridImage}
                      src={getBannerImageURL(
                        appConfig?.deviceWidth as number,
                        bannerImage
                      )}
                      alt={bannerImage.title}
                      title={bannerImage.title}
                    />
                  </div>
                );
              })}
          </div>
        ) : null}
      </>
    );
  }, [bannerImages]);

  // render content section
  const renderContent = () => {
    const {
      renderIcon,
      imageproperties,
      couponText,
      disableClick,
      isCohortDiscount,
      cohertDiscountedText
    } = renderCouponProperties();
    return (
      <div css={style.contentSection}>
        <h2 css={style.title}>{subscription?.CMS_BOTTOMSHEET_HEADER_TEXT}</h2>
        {isCohortDiscount && (
          <div css={style.cohortInfo}>
            <div>
              <img
                src={imageproperties?.imageSrc}
                alt={imageproperties?.imageAlt}
                height={14}
                width={14}
              />
            </div>
            <div css={style.cohortInfoTxt}>{cohertDiscountedText}</div>
          </div>
        )}
        {defaultPlan && (
          <HighestPlanPage
            plansData={defaultPlan}
            newSubscriptionStrings={subscription}
            showDescriptionAndScreenCount={true}
            renderSuggestedText={true}
            renderTick={true}
            renderCardFooter={true}
            isDiscount={isCohortDiscount}
          />
        )}
        <div
          css={style.applyCodeBox}
          onClick={disableClick ? undefined : applyCodeHandler}
          data-testid="apply-code-btn">
          <div css={style.couponImgContainer}>
            <img
              src={imageproperties?.imageSrc}
              alt={imageproperties?.imageAlt}
              height={14}
              width={14}
            />
          </div>
          <div css={style.couponTextContainer}>{couponText}</div>
          {renderIcon && (
            <div>
              <span css={style.viewAllIcon}></span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // render footer payment section
  const renderPaymentSection = () => {
    return (
      <>
        <div css={style.popupPaymentContainer}>
          <div css={style.mainSection}>
            <div
              css={style.upiSection}
              onClick={handlePaymentRedirection}
              data-testid="pay-using-upi-btn">
              <div css={style.upiText}>
                <div css={style.text}>
                  {subscription.CMS_DEFAULTPAYMENT_TEXT}
                </div>
                <div css={style.icon}></div>
              </div>
            </div>
            <div css={style.buyPremiumSection}>
              <Button
                customStyles={style.premiumBtn}
                callback={handlePaymentRedirection}
                id={`${subscription.BUY_PREMIUM_CTA.replaceAll(
                  ' ',
                  '-'
                ).toLowerCase()}-btn`}>
                {subscription.BUY_PREMIUM_CTA}
              </Button>
            </div>
          </div>
          <div css={style.loginMsgContainer}>
            {getLocalStorage(USER_TOKEN) ? (
              <span>
                {subscription.Subscription_Bottomsheet_Already_Loggedin_Text}
                <span css={style.loginText}>
                  {usersData?.mobile || usersData?.email}
                </span>
              </span>
            ) : (
              <span>
                {subscription.CMS_BOTTOMSHEET_ALREADYSUB_TEXT}

                <a href={HeaderLinks.SIGN_IN} onClick={signInClickHandler}>
                  <span css={style.loginText} data-testid="login-btn">
                    {subscription.CMS_BOTTOMSHEET_LOGIN_TEXT}
                  </span>
                </a>
              </span>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Modal
        show={showBuyPlanFlag}
        onClose={playPauseClickHandler}
        width="416px"
        height="464px"
        background={colors.AppBgColor}
        renderCloseIcon={!applyCodeFlag}
        closeIconColor={colors.white}
        padding={applyCodeFlag ? '40px 24px' : '0'}>
        <>
          {showBuyPlanFlag && !applyCodeFlag && (
            <>
              {renderBannerImages()}
              {renderContent()}
              {renderPaymentSection()}
            </>
          )}

          {applyCodeFlag && (
            <ApplyCodePopup
              xAccessToken={xAccessToken}
              show={applyCodeFlag}
              onClose={applyCodeHandler}
              updateDiscount={updateDiscount}
              newSubscriptionStrings={subscription}
              countryCode={countryCode}
              stateCode={stateCode}
              checkLoggedIn={checkLoggedIn}
              prepaidCoupon={prepaidCoupon}
              renderContentOnly={true}
              consumptionBuyPlan={true}
            />
          )}
        </>
      </Modal>
    </>
  );
};

export default ConsumptionBuyPlanPopup;
